/**
 * CarouselGrid — Horizontal paginated grid with drag/swipe
 *
 * Desktop: CSS Grid with overflow hidden, JS pagination + mouse drag.
 * Mobile:  Same transform approach, touch swipe, no pagination UI.
 *
 * Usage:
 *   new CarouselGrid(document.getElementById('gallery-grid'));
 */

var CarouselGrid = (function() {

  function CarouselGrid(el) {
    this.el = el;
    this.viewport = el.querySelector('.viewport');
    this.track = el.querySelector('.track');
    this.paginationEl = el.querySelector('[comp="pagination"]');
    this.dotsContainer = this.paginationEl ? this.paginationEl.querySelector('.dots') : null;
    this.prevBtn = this.paginationEl ? this.paginationEl.querySelector('.arrow.prev') : null;
    this.nextBtn = this.paginationEl ? this.paginationEl.querySelector('.arrow.next') : null;
    this.currentPage = 0;
    this.totalPages = 1;
    this.isMobile = false;

    // Expose instance on DOM element for external access (e.g. filters)
    el._carouselGrid = this;

    this._init();
  }

  CarouselGrid.prototype._init = function() {
    var self = this;

    this._checkMobile();

    // Wait for images before calculating layout
    if (typeof imagesLoaded !== 'undefined') {
      imagesLoaded(this.track, function() {
        self.calculatePages();
        self.renderDots();
        self.updateState();
      });
    } else {
      // Fallback: calculate after a short delay
      setTimeout(function() {
        self.calculatePages();
        self.renderDots();
        self.updateState();
      }, 300);
    }

    // Arrow click handlers
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function() {
        self.goToPage(self.currentPage - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function() {
        self.goToPage(self.currentPage + 1);
      });
    }

    // Drag / swipe
    this._initDrag();

    // Debounced resize
    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        self._checkMobile();
        self.calculatePages();
        self.renderDots();
        self.goToPage(Math.min(self.currentPage, self.totalPages - 1));
      }, 200);
    });
  };

  CarouselGrid.prototype._checkMobile = function() {
    this.isMobile = window.innerWidth < 768; // md breakpoint
  };

  CarouselGrid.prototype.calculatePages = function() {
    var viewW = this.viewport.clientWidth;
    var trackW = this.track.scrollWidth;
    this.totalPages = Math.max(1, Math.ceil(trackW / viewW));
  };

  CarouselGrid.prototype.goToPage = function(page) {
    this.currentPage = Math.max(0, Math.min(page, this.totalPages - 1));
    var offset = this.currentPage * this.viewport.clientWidth;
    this.track.style.transform = 'translateX(-' + offset + 'px)';
    this.updateState();
  };

  CarouselGrid.prototype.renderDots = function() {
    if (!this.dotsContainer) { return; }
    var self = this;
    this.dotsContainer.innerHTML = '';

    for (var i = 0; i < this.totalPages; i++) {
      var dot = document.createElement('button');
      dot.className = 'dot' + (i === this.currentPage ? ' --active' : '');
      dot.dataset.page = i;
      dot.setAttribute('aria-label', 'Pagina ' + (i + 1));
      (function(pageIndex) {
        dot.addEventListener('click', function() {
          self.goToPage(pageIndex);
        });
      })(i);
      this.dotsContainer.appendChild(dot);
    }
  };

  CarouselGrid.prototype.updateState = function() {
    // Update dots
    if (this.dotsContainer) {
      var dots = this.dotsContainer.querySelectorAll('.dot');
      var self = this;
      dots.forEach(function(d, i) {
        if (i === self.currentPage) {
          d.classList.add('--active');
        } else {
          d.classList.remove('--active');
        }
      });
    }

    // Update arrows
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentPage === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentPage >= this.totalPages - 1;
    }
  };

  // ── Public: recalculate after external changes (e.g. filter hide/show) ──
  CarouselGrid.prototype.refresh = function() {
    this.calculatePages();
    this.renderDots();
    this.goToPage(0);
  };

  // ── Drag / Swipe — Pointer Events + touch-action: pan-y ──
  // Browser handles vertical scroll natively (via CSS touch-action: pan-y).
  // JS handles horizontal drag via unified Pointer Events API (mouse + touch + pen).

  CarouselGrid.prototype._initDrag = function() {
    var self = this;
    var startX = 0;
    var startY = 0;
    var startTranslate = 0;
    var isDragging = false;
    var dragDelta = 0;
    var startTime = 0;
    var directionLocked = false;
    var isHorizontal = false;

    // Prevent native browser drag on links/images — without this,
    // dragging from a tile starts a ghost-image drag and steals pointer events
    this.track.addEventListener('dragstart', function(e) {
      e.preventDefault();
    });

    function getTranslateX() {
      var style = window.getComputedStyle(self.track);
      var matrix = style.transform;
      if (!matrix || matrix === 'none') { return 0; }
      var values = matrix.match(/matrix.*\((.+)\)/);
      if (values) {
        var parts = values[1].split(', ');
        return parseFloat(parts[4]) || 0;
      }
      return 0;
    }

    this.viewport.addEventListener('pointerdown', function(e) {
      if (e.button !== 0) { return; } // primary button only
      isDragging = true;
      directionLocked = false;
      isHorizontal = false;
      startX = e.clientX;
      startY = e.clientY;
      startTranslate = getTranslateX();
      dragDelta = 0;
      startTime = Date.now();
      self.track.style.transition = 'none';
      // Don't capture here — capture only after horizontal lock,
      // otherwise click events get redirected to viewport and links stop working
    });

    this.viewport.addEventListener('pointermove', function(e) {
      if (!isDragging) { return; }

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      // Directional lock: decide once after 5px of movement
      if (!directionLocked && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        directionLocked = true;
        isHorizontal = Math.abs(dx) >= Math.abs(dy);
        if (isHorizontal) {
          self.el.classList.add('--dragging');
          // Capture pointer now that we know it's a horizontal drag
          try { self.viewport.setPointerCapture(e.pointerId); } catch (err) {}
        } else {
          // Vertical scroll — abort drag, let browser handle
          isDragging = false;
          self.track.style.transition = '';
          return;
        }
      }

      if (!isHorizontal) { return; }

      dragDelta = dx;
      self.track.style.transform = 'translateX(' + (startTranslate + dragDelta) + 'px)';
    });

    this.viewport.addEventListener('pointerup', function(e) {
      if (!isDragging) { return; }
      isDragging = false;
      self.el.classList.remove('--dragging');
      if (isHorizontal) {
        try { self.viewport.releasePointerCapture(e.pointerId); } catch (err) {}
      }

      if (!isHorizontal) {
        self.track.style.transition = '';
        return;
      }

      // Free drag with momentum — no page snap
      var elapsed = Date.now() - startTime;
      var velocity = elapsed > 0 ? dragDelta / elapsed : 0; // px/ms, signed
      var currentPos = startTranslate + dragDelta;
      var momentum = velocity * 300;
      var targetPos = currentPos + momentum;

      // Clamp within bounds [-(maxScroll), 0]
      var maxScroll = self.track.scrollWidth - self.viewport.clientWidth;
      if (maxScroll < 0) { maxScroll = 0; }
      targetPos = Math.max(-maxScroll, Math.min(targetPos, 0));

      // XS (<576): snap to nearest tile (col-width + gap) so that drag always
      // stops on a full tile + peek of the next one. Above XS, free drag stays.
      if (window.matchMedia('(max-width: 575.98px)').matches) {
        var trackStyle = window.getComputedStyle(self.track);
        var gap = parseFloat(trackStyle.columnGap) || 0;
        // --col-width computes to an unresolved calc() string (cqi units),
        // not parseable as a number — measure the rendered tile instead
        var firstTile = self.track.querySelector('article.item:not(.--hidden)') || self.track.firstElementChild;
        var colWidth = firstTile
          ? firstTile.getBoundingClientRect().width
          : (self.viewport.clientWidth - gap) / 1.25;
        var step = colWidth + gap;
        if (step > 0) {
          var snapIndex = Math.round(-targetPos / step);
          targetPos = -snapIndex * step;
          targetPos = Math.max(-maxScroll, Math.min(targetPos, 0));
        }
      }

      // Smooth deceleration via CSS transition
      self.track.style.transition = '';
      self.track.style.transform = 'translateX(' + targetPos + 'px)';

      // Sync page indicator to nearest page (for arrows/dots)
      self.currentPage = Math.round(Math.abs(targetPos) / self.viewport.clientWidth);
      self.currentPage = Math.max(0, Math.min(self.currentPage, self.totalPages - 1));
      self.updateState();
    });

    // Prevent click navigation when user was dragging
    this.viewport.addEventListener('click', function(e) {
      if (Math.abs(dragDelta) > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // pointercancel (incoming call, system gesture, etc.)
    this.viewport.addEventListener('pointercancel', function() {
      if (!isDragging) { return; }
      isDragging = false;
      self.el.classList.remove('--dragging');
      self.track.style.transition = '';
      self.goToPage(self.currentPage); // snap back
    });
  };

  return CarouselGrid;

})();
